import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import toast from "react-hot-toast";
import SEO from "@/components/SEO";
import {
    CreditCard,
    CheckCircle,
    Clock,
    XCircle,
    AlertCircle,
    Search,
    Save,
    ChevronDown,
    ChevronUp,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

const STATUS_OPTIONS = [
    { value: "", label: "সব" },
    { value: "pending", label: "পেন্ডিং" },
    { value: "active", label: "সক্রিয়" },
    { value: "expired", label: "মেয়াদ শেষ" },
    { value: "rejected", label: "প্রত্যাখ্যাত" },
];

const STATUS_CFG = {
    pending: {
        label: "পেন্ডিং",
        icon: Clock,
        cls: "text-yellow-600 bg-yellow-50 border-yellow-300",
    },
    active: {
        label: "সক্রিয়",
        icon: CheckCircle,
        cls: "text-green-600  bg-green-50  border-green-300",
    },
    expired: {
        label: "মেয়াদ শেষ",
        icon: AlertCircle,
        cls: "text-slate-500  bg-slate-50  border-slate-300",
    },
    rejected: {
        label: "প্রত্যাখ্যাত",
        icon: XCircle,
        cls: "text-red-600    bg-red-50    border-red-300",
    },
};

const DEFAULT_PLANS = [
    { days: 7, price: 49, label: "৭ দিন" },
    { days: 15, price: 79, label: "১৫ দিন" },
    { days: 30, price: 129, label: "৩০ দিন" },
    { days: 60, price: 199, label: "৬০ দিন" },
];

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("bn-BD", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso.slice(0, 10);
    }
}

// ─── Main Page ────────────────────────────────────────────────────────
export default function AdminPayments() {
    const [activeTab, setActiveTab] = useState("requests"); // "requests" | "settings"

    return (
        <div className="space-y-6">
            <SEO title="Admin - Payments" />
            <div className="flex items-center gap-2 sm:gap-3">
                <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-brand-royal shrink-0" />
                <h1 className="text-lg sm:text-2xl font-bold text-brand-navy">ই-বুক পেমেন্ট ম্যানেজমেন্ট</h1>
            </div>

            {/* Tab switcher */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
                <button
                    onClick={() => setActiveTab("requests")}
                    className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-semibold transition whitespace-nowrap ${activeTab === "requests" ? "bg-white text-brand-navy shadow-sm" : "text-slate-500 hover:text-brand-navy"}`}
                >
                    পেমেন্ট রিকোয়েস্ট
                </button>
                <button
                    onClick={() => setActiveTab("settings")}
                    className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-semibold transition whitespace-nowrap ${activeTab === "settings" ? "bg-white text-brand-navy shadow-sm" : "text-slate-500 hover:text-brand-navy"}`}
                >
                    পেমেন্ট সেটিংস
                </button>
            </div>

            {activeTab === "requests" && <RequestsTab />}
            {activeTab === "settings" && <SettingsTab />}
        </div>
    );
}

// ─── Requests Tab ─────────────────────────────────────────────────────
function RequestsTab() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState(null); // sub id

    const fetchData = async (p = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status) params.append("status", status);
            if (q.trim()) params.append("q", q.trim());
            params.append("page", p);
            params.append("per_page", 20);
            const r = await api.get(`/admin/ebook-subscriptions?${params}`);
            setItems(r.data.items || []);
            setTotal(r.data.total);
            setPage(r.data.page);
            setTotalPages(r.data.total_pages);
        } catch {
            toast.error("ডেটা লোড হয়নি");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
        fetchData(1);
    }, [status]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchData(1);
    };

    return (
        <div className="space-y-4">
            {/* Summary bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                {STATUS_OPTIONS.slice(1).map((s) => {
                    const cnt = items.filter((i) => i.status === s.value).length;
                    const cfg = STATUS_CFG[s.value];
                    const Icon = cfg.icon;
                    return (
                        <button
                            key={s.value}
                            onClick={() => setStatus(s.value === status ? "" : s.value)}
                            className={`rounded-xl border p-3 text-left transition ${status === s.value ? "ring-2 ring-brand-royal" : ""} ${cfg.cls}`}
                        >
                            <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                <span className="text-xs font-semibold">{s.label}</span>
                            </div>
                            <div className="text-2xl font-bold mt-1">{cnt}</div>
                        </button>
                    );
                })}
            </div>

            {/* Search + filter */}
            <form onSubmit={handleSearch} className="flex gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[160px] sm:min-w-48">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="নাম, ইমেইল, TxnID..."
                        className="w-full pl-9 pr-4 py-2 sm:py-2.5 rounded-xl border border-slate-200 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand-royal/30"
                    />
                </div>
                <button
                    type="submit"
                    className="btn-primary px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center gap-1.5"
                >
                    <Search className="w-4 h-4" /> খুঁজুন
                </button>
                <button
                    type="button"
                    onClick={fetchData}
                    className="px-3 py-2 sm:py-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition"
                >
                    <RefreshCw className="w-4 h-4" />
                </button>
            </form>

            {/* Table */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-brand-royal/20 border-t-brand-royal rounded-full animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                    <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>কোনো পেমেন্ট রিকোয়েস্ট নেই</p>
                </div>
            ) : (
                <>
                    <div className="space-y-2">
                        {items.map((sub) => (
                            <SubscriptionRow
                                key={sub.id}
                                sub={sub}
                                expanded={expanded === sub.id}
                                onToggle={() => setExpanded(expanded === sub.id ? null : sub.id)}
                                onUpdate={(updated) =>
                                    setItems((prev) =>
                                        prev.map((s) => (s.id === updated.id ? updated : s)),
                                    )
                                }
                            />
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 pt-4">
                            <button
                                onClick={() => fetchData(Math.max(1, page - 1))}
                                disabled={page <= 1}
                                className="p-1.5 rounded border border-slate-200 disabled:opacity-30 hover:bg-slate-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                                .map((p, idx, arr) => (
                                    <span key={p} className="flex items-center gap-1">
                                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                                            <span className="px-1 text-slate-400">...</span>
                                        )}
                                        <button
                                            onClick={() => fetchData(p)}
                                            className={`min-w-[32px] h-8 rounded text-xs font-medium ${
                                                p === page
                                                    ? "btn-primary"
                                                    : "border border-slate-200 hover:bg-slate-50"
                                            }`}
                                        >
                                            {p}
                                        </button>
                                    </span>
                                ))}

                            <button
                                onClick={() => fetchData(Math.min(totalPages, page + 1))}
                                disabled={page >= totalPages}
                                className="p-1.5 rounded border border-slate-200 disabled:opacity-30 hover:bg-slate-50"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>

                            <span className="text-xs text-slate-400 ml-2">
                                {total} total
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ─── Single Subscription Row ──────────────────────────────────────────
function SubscriptionRow({ sub, expanded, onToggle, onUpdate }) {
    const cfg = STATUS_CFG[sub.status] || STATUS_CFG.pending;
    const Icon = cfg.icon;

    const [saving, setSaving] = useState(false);
    const [newStatus, setNewStatus] = useState(sub.status);
    const [expireDate, setExpireDate] = useState(
        sub.expire_date ? sub.expire_date.slice(0, 10) : "",
    );
    const [note, setNote] = useState(sub.note || "");

    const handleSave = async () => {
        setSaving(true);
        try {
            const r = await api.put(`/admin/ebook-subscriptions/${sub.id}`, {
                status: newStatus,
                expire_date: expireDate || null,
                note: note,
            });
            onUpdate(r.data);
            toast.success("আপডেট হয়েছে!");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "আপডেট ব্যর্থ হয়েছে");
        } finally {
            setSaving(false);
        }
    };

    // Quick approve — set active, auto expire date from plan
    const handleQuickApprove = async () => {
        setSaving(true);
        try {
            const r = await api.put(`/admin/ebook-subscriptions/${sub.id}`, {
                status: "active",
                note: note,
            });
            onUpdate(r.data);
            toast.success("✅ সাবস্ক্রিপশন সক্রিয় করা হয়েছে!");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "আপডেট ব্যর্থ হয়েছে");
        } finally {
            setSaving(false);
        }
    };

    const handleQuickReject = async () => {
        if (!window.confirm("এই পেমেন্ট রিকোয়েস্ট প্রত্যাখ্যান করবেন?")) return;
        setSaving(true);
        try {
            const r = await api.put(`/admin/ebook-subscriptions/${sub.id}`, {
                status: "rejected",
                note: note || "পেমেন্ট যাচাই হয়নি",
            });
            onUpdate(r.data);
            toast.success("প্রত্যাখ্যান করা হয়েছে");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "আপডেট ব্যর্থ হয়েছে");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border border-slate-200 rounded-2xl overflow-hidden">
            {/* Row Header */}
            <div
                className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white hover:bg-slate-50/50 cursor-pointer"
                onClick={onToggle}
            >
                {/* Status badge */}
                <div
                    className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-full border text-[10px] sm:text-xs font-semibold flex-shrink-0 ${cfg.cls}`}
                >
                    <Icon className="w-3 h-3" />
                    {cfg.label}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 space-y-1 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-1 text-sm">
                    <div>
                        <p className="font-semibold text-brand-navy truncate text-xs sm:text-sm">{sub.user_name}</p>
                        <p className="text-[10px] sm:text-xs text-slate-500 truncate">{sub.user_email}</p>
                    </div>
                    <div className="hidden sm:block">
                        <p className="text-brand-navy truncate text-xs sm:text-sm">{sub.book_title}</p>
                        <p className="text-[10px] sm:text-xs text-slate-500">
                            {sub.plan_days} দিন • ৳{sub.plan_price}
                        </p>
                    </div>
                    <div className="hidden sm:block">
                        <p className="font-mono text-[10px] sm:text-xs text-slate-600">TxnID: {sub.txn_id}</p>
                        <p className="text-[10px] sm:text-xs text-slate-500">Sender: {sub.sender_number}</p>
                    </div>
                </div>

                {/* Quick actions for pending */}
                {sub.status === "pending" && (
                    <div className="flex gap-1.5 sm:gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={handleQuickApprove}
                            disabled={saving}
                            className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-green-600 text-white text-[10px] sm:text-xs font-bold hover:bg-green-700 disabled:opacity-50 transition"
                        >
                            ✓
                        </button>
                        <button
                            onClick={handleQuickReject}
                            disabled={saving}
                            className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-red-500 text-white text-[10px] sm:text-xs font-bold hover:bg-red-600 disabled:opacity-50 transition"
                        >
                            ✕
                        </button>
                    </div>
                )}

                <div className="text-slate-400 flex-shrink-0">
                    {expanded ? (
                        <ChevronUp className="w-4 h-4" />
                    ) : (
                        <ChevronDown className="w-4 h-4" />
                    )}
                </div>
            </div>

            {/* Expanded detail editor */}
            {expanded && (
                <div className="border-t border-slate-100 bg-slate-50 p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
                        <div>
                            <span className="text-slate-500 text-[10px] sm:text-xs">জমার তারিখ</span>
                            <p className="font-semibold">{fmt(sub.created_at)}</p>
                        </div>
                        <div>
                            <span className="text-slate-500 text-[10px] sm:text-xs">শুরুর তারিখ</span>
                            <p className="font-semibold">{fmt(sub.start_date)}</p>
                        </div>
                        <div>
                            <span className="text-slate-500 text-[10px] sm:text-xs">মেয়াদ শেষ</span>
                            <p className="font-semibold">{fmt(sub.expire_date)}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                        {/* Status */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                                স্ট্যাটাস পরিবর্তন
                            </label>
                            <select
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-royal/30"
                            >
                                <option value="pending">পেন্ডিং</option>
                                <option value="active">সক্রিয়</option>
                                <option value="expired">মেয়াদ শেষ</option>
                                <option value="rejected">প্রত্যাখ্যাত</option>
                            </select>
                        </div>

                        {/* Expire date */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                                মেয়াদ শেষের তারিখ (ম্যানুয়াল)
                            </label>
                            <input
                                type="date"
                                value={expireDate}
                                onChange={(e) => setExpireDate(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-royal/30"
                            />
                            <p className="text-xs text-slate-400 mt-0.5">
                                খালি রাখলে plan অনুযায়ী auto set হবে
                            </p>
                        </div>

                        {/* Note */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                                নোট (ঐচ্ছিক)
                            </label>
                            <input
                                type="text"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Admin note..."
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-royal/30"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="btn-primary px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-60"
                        >
                            {saving ? (
                                <svg
                                    className="animate-spin w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="white"
                                        strokeWidth="3"
                                        strokeDasharray="60"
                                        strokeDashoffset="15"
                                    />
                                </svg>
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            সেভ করুন
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Settings Tab ─────────────────────────────────────────────────────
function SettingsTab() {
    const [form, setForm] = useState({
        bkash_number: "",
        nagad_number: "",
        rocket_number: "",
        instructions: "",
        plans: DEFAULT_PLANS,
    });
    const [loading, setSaving] = useState(false);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        api.get("/admin/ebook-payment-settings")
            .then((r) => {
                if (r.data && Object.keys(r.data).length > 0) {
                    setForm({
                        bkash_number: r.data.bkash_number || "",
                        nagad_number: r.data.nagad_number || "",
                        rocket_number: r.data.rocket_number || "",
                        instructions: r.data.instructions || "",
                        plans: r.data.plans?.length > 0 ? r.data.plans : DEFAULT_PLANS,
                    });
                }
            })
            .catch(() => toast.error("সেটিংস লোড হয়নি"))
            .finally(() => setFetching(false));
    }, []);

    const updatePlan = (idx, field, val) => {
        const plans = [...form.plans];
        plans[idx] = { ...plans[idx], [field]: field === "price" ? parseFloat(val) || 0 : val };
        setForm({ ...form, plans });
    };

    const handleSave = async () => {
        if (!form.bkash_number && !form.nagad_number && !form.rocket_number) {
            toast.error("অন্তত একটি পেমেন্ট নম্বর দিন");
            return;
        }
        setSaving(true);
        try {
            await api.put("/admin/ebook-payment-settings", form);
            toast.success("সেটিংস সেভ হয়েছে!");
        } catch {
            toast.error("সেভ ব্যর্থ হয়েছে");
        } finally {
            setSaving(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-brand-royal/20 border-t-brand-royal rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl space-y-6">
            {/* Payment Numbers */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
                <h3 className="font-bold text-brand-navy">পেমেন্ট নম্বর</h3>

                {[
                    {
                        key: "bkash_number",
                        label: "bKash নম্বর",
                        placeholder: "01XXXXXXXXX",
                        color: "border-pink-300 focus:ring-pink-200",
                    },
                    {
                        key: "nagad_number",
                        label: "Nagad নম্বর",
                        placeholder: "01XXXXXXXXX",
                        color: "border-orange-300 focus:ring-orange-200",
                    },
                    {
                        key: "rocket_number",
                        label: "Rocket নম্বর",
                        placeholder: "01XXXXXXXXX",
                        color: "border-purple-300 focus:ring-purple-200",
                    },
                ].map(({ key, label, placeholder, color }) => (
                    <div key={key}>
                        <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                            {label}
                        </label>
                        <input
                            type="tel"
                            value={form[key]}
                            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                            placeholder={placeholder}
                            className={`w-full px-4 py-2.5 rounded-xl border text-sm bg-white text-brand-navy focus:outline-none focus:ring-2 ${color}`}
                        />
                    </div>
                ))}

                <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-1.5">
                        অতিরিক্ত নির্দেশনা (ঐচ্ছিক)
                    </label>
                    <textarea
                        value={form.instructions}
                        onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                        rows={3}
                        placeholder="যেমন: Send Money করুন, Merchant নয়..."
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-royal/30 resize-none"
                    />
                </div>
            </div>

            <button
                onClick={handleSave}
                disabled={loading}
                className="btn-primary w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
                {loading ? (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="white"
                            strokeWidth="3"
                            strokeDasharray="60"
                            strokeDashoffset="15"
                        />
                    </svg>
                ) : (
                    <Save className="w-4 h-4" />
                )}
                সেটিংস সেভ করুন
            </button>
        </div>
    );
}
